import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Pencil, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useFetch } from "@/hooks/useFetch";
import { TASK_STATUS, PRIORITY, fmtDate, fmtDateTime, todayZurich } from "@/lib/format";
import { MapBadge, StatusBadge } from "@/components/StatusBadge";
import { Card, Empty, PageHeader, Sel, mapOpts } from "@/components/Field";
import { TaskDialog } from "@/components/dialogs/TaskDialog";

const soon = (d) => {
  const t = new Date(todayZurich());
  t.setDate(t.getDate() + 7);
  return d && d >= todayZurich() && d <= t.toISOString().slice(0, 10);
};
const open = (t) => ["todo", "in_progress"].includes(t.status);

const TaskRow = ({ t, onOpen }) => (
  <li>
    <button data-testid={`task-row-${t.id}`} onClick={() => onOpen(t)} className="w-full flex items-center justify-between gap-3 px-4 sm:px-5 py-3 text-left hover:bg-slate-50">
      <div className="min-w-0">
        <div className="text-[15px] font-semibold text-slate-900">{t.title}</div>
        <div className="text-sm text-slate-500">{t.assignee_names?.join(", ")}{t.due_date && ` · scad. ${fmtDate(t.due_date)}`}{t.event_name && ` · ${t.event_name}`}</div>
      </div>
      <div className="flex flex-wrap justify-end gap-1.5 shrink-0">
        {t.overdue && <StatusBadge label="Scaduto" tone="danger" />}
        {t.priority !== "normal" && <MapBadge map={PRIORITY} value={t.priority} />}
        <MapBadge map={TASK_STATUS} value={t.status} />
      </div>
    </button>
  </li>
);

const Section = ({ title, items, onOpen, id }) => items.length > 0 && (
  <Card testId={`tasks-section-${id}`}>
    <h2 className="font-heading font-semibold px-5 h-12 flex items-center border-b">{title} <span className="ml-2 text-slate-400">{items.length}</span></h2>
    <ul className="divide-y">{items.map((t) => <TaskRow key={t.id} t={t} onOpen={onOpen} />)}</ul>
  </Card>
);

const TaskDetail = ({ task, onClose, onChanged, onEdit }) => {
  const { user, can } = useAuth();
  const [comment, setComment] = useState("");
  if (!task) return null;
  const manager = can("tasks", "create");
  const mine = task.assignees.includes(user.id);
  const setStatus = async (status) => {
    try {
      await api.patch(`/tasks/${task.id}`, { status });
      toast.success("Stato aggiornato");
      onChanged();
    } catch (e) { toast.error(formatError(e)); }
  };
  const send = async () => {
    if (!comment.trim()) return;
    try {
      await api.post(`/tasks/${task.id}/comments`, { text: comment });
      setComment("");
      onChanged();
    } catch (e) { toast.error(formatError(e)); }
  };
  return (
    <Dialog open={!!task} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="task-detail" className="sm:max-w-xl max-h-[92vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-heading text-xl pr-6">{task.title}</DialogTitle></DialogHeader>
        <div className="flex flex-wrap gap-1.5"><MapBadge map={TASK_STATUS} value={task.status} />{task.overdue && <StatusBadge label="Scaduto" tone="danger" />}<MapBadge map={PRIORITY} value={task.priority} /></div>
        {task.description && <p className="text-slate-700 whitespace-pre-wrap">{task.description}</p>}
        <dl className="grid grid-cols-2 gap-2 text-sm">
          <dt className="text-slate-500">Responsabili</dt><dd>{task.assignee_names.join(", ")}</dd>
          <dt className="text-slate-500">Assegnato da</dt><dd>{task.assigned_by_name} · {fmtDate(task.created_at)}</dd>
          <dt className="text-slate-500">Scadenza</dt><dd>{task.due_date ? fmtDate(task.due_date) : "—"}</dd>
          {task.event_name && <><dt className="text-slate-500">Evento</dt><dd>{task.event_name}</dd></>}
          {task.completed_by_name && <><dt className="text-slate-500">Completato da</dt><dd>{task.completed_by_name} · {fmtDateTime(task.completed_at)}</dd></>}
        </dl>
        {(mine || manager) && (
          <div className="flex flex-wrap gap-2">
            {task.status === "todo" && <Button data-testid="task-start-button" className="h-11" onClick={() => setStatus("in_progress")}>Inizia</Button>}
            {open(task) && <Button data-testid="task-complete-button" className="h-11 bg-green-700 hover:bg-green-800" onClick={() => setStatus("done")}>Segna come completato</Button>}
            {task.status === "done" && <Button data-testid="task-reopen-button" variant="outline" className="h-11" onClick={() => setStatus("todo")}>Riapri</Button>}
            {manager && open(task) && <Button data-testid="task-cancel-button" variant="ghost" className="h-11 text-red-700" onClick={() => setStatus("cancelled")}>Annulla compito</Button>}
            {manager && <Button data-testid="task-edit-button" variant="outline" className="h-11" onClick={() => onEdit(task)}><Pencil className="h-4 w-4 mr-1" /> Modifica / riassegna</Button>}
          </div>
        )}
        <div className="space-y-2">
          <h3 className="font-semibold text-slate-900">Commenti</h3>
          {task.comments.length === 0 && <p className="text-sm text-slate-500">Nessun commento.</p>}
          {task.comments.map((c) => <div key={c.id} className="rounded-md bg-slate-50 px-3 py-2 text-sm"><strong>{c.by_name}</strong> <span className="text-slate-400">{fmtDateTime(c.at)}</span><div className="whitespace-pre-wrap">{c.text}</div></div>)}
          <div className="flex gap-2">
            <Textarea data-testid="task-comment-input" value={comment} onChange={(e) => setComment(e.target.value)} rows={2} placeholder="Scrivi un commento" />
            <Button data-testid="task-comment-send" className="h-auto" onClick={send}><Send className="h-4 w-4" /></Button>
          </div>
        </div>
        <details className="text-sm text-slate-600"><summary className="cursor-pointer font-semibold">Storico modifiche</summary>
          <ul className="mt-2 space-y-1">{task.history.map((h, i) => <li key={i}>{fmtDateTime(h.at)} · {h.by_name} · {h.action === "created" ? "creato" : `modificato (${(h.fields || []).join(", ")})`}</li>)}</ul>
        </details>
      </DialogContent>
    </Dialog>
  );
};

export default function TasksPage() {
  const { can } = useAuth();
  const manager = can("tasks", "create");
  const [sp, setSp] = useSearchParams();
  const [scope, setScope] = useState("mine");
  const [f, setF] = useState({ status: "", priority: "", assignee: "", event_id: "", due: "" });
  const [dialog, setDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const qs = new URLSearchParams(Object.fromEntries(Object.entries({ scope, ...(scope === "all" ? f : {}) }).filter(([, v]) => v)));
  const { data, reload } = useFetch(`/tasks?${qs}`, [], { poll: 30000 });
  const members = useFetch("/members").data || [];
  const events = useFetch("/events").data || [];
  const selected = data?.find((t) => t.id === sp.get("t")) || null;
  const openTask = (t) => setSp(t ? { t: t.id } : {});

  useEffect(() => {
    if (sp.get("t") && data && !selected && scope === "mine" && manager) setScope("all");
  }, [sp, data, selected, scope, manager]);

  const items = data || [];
  return (
    <>
      <PageHeader title="Attività e compiti">
        {manager && <Button data-testid="tasks-new-button" className="h-11" onClick={() => setDialog(true)}><Plus className="h-4 w-4 mr-1" /> Crea compito</Button>}
      </PageHeader>
      {manager && (
        <Tabs value={scope} onValueChange={setScope}>
          <TabsList className="h-11">
            <TabsTrigger value="mine" className="h-9 px-4" data-testid="tasks-tab-mine">I miei compiti</TabsTrigger>
            <TabsTrigger value="all" className="h-9 px-4" data-testid="tasks-tab-all">Tutti i compiti</TabsTrigger>
          </TabsList>
        </Tabs>
      )}
      {scope === "all" && (
        <Card className="p-4 grid grid-cols-2 md:grid-cols-5 gap-2">
          <Sel testId="tasks-filter-assignee" value={f.assignee} onChange={(v) => setF({ ...f, assignee: v })} placeholder="Tutti i responsabili" options={members.map((m) => ({ value: m.user_id, label: m.name }))} />
          <Sel testId="tasks-filter-event" value={f.event_id} onChange={(v) => setF({ ...f, event_id: v })} placeholder="Tutti gli eventi" options={events.map((e) => ({ value: e.id, label: e.name }))} />
          <Sel testId="tasks-filter-status" value={f.status} onChange={(v) => setF({ ...f, status: v })} placeholder="Tutti gli stati" options={mapOpts(TASK_STATUS)} />
          <Sel testId="tasks-filter-priority" value={f.priority} onChange={(v) => setF({ ...f, priority: v })} placeholder="Tutte le priorità" options={mapOpts(PRIORITY)} />
          <Sel testId="tasks-filter-due" value={f.due} onChange={(v) => setF({ ...f, due: v })} placeholder="Qualsiasi scadenza" options={[{ value: "overdue", label: "Scaduti" }, { value: "soon", label: "In scadenza (7 giorni)" }]} />
        </Card>
      )}
      {data && items.length === 0 && <Empty testId="tasks-empty" text="Nessun compito." />}
      {scope === "mine" ? (
        <div className="space-y-4">
          <Section id="overdue" title="Scaduti" items={items.filter((t) => t.overdue)} onOpen={openTask} />
          <Section id="soon" title="In scadenza (7 giorni)" items={items.filter((t) => open(t) && !t.overdue && soon(t.due_date))} onOpen={openTask} />
          <Section id="todo" title="Da fare" items={items.filter((t) => t.status === "todo" && !t.overdue && !soon(t.due_date))} onOpen={openTask} />
          <Section id="in-progress" title="In corso" items={items.filter((t) => t.status === "in_progress" && !t.overdue && !soon(t.due_date))} onOpen={openTask} />
          <Section id="done" title="Completati" items={items.filter((t) => t.status === "done")} onOpen={openTask} />
        </div>
      ) : items.length > 0 && <Card><ul className="divide-y">{items.map((t) => <TaskRow key={t.id} t={t} onOpen={openTask} />)}</ul></Card>}
      <TaskDetail task={selected} onClose={() => openTask(null)} onChanged={reload} onEdit={(t) => setEditing(t)} />
      <TaskDialog open={dialog || !!editing} task={editing} onOpenChange={(o) => { if (!o) { setDialog(false); setEditing(null); } }} onSaved={reload} />
    </>
  );
}
